/**
 * Usage-weighted ordering for toolbar / dock buttons.
 *
 * Persists per-scope click counts in localStorage. Items the operator
 * uses most often float left; never-used items keep their declared
 * default order as a tiebreaker.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

const STORAGE_PREFIX = 'quotech.action-usage'

export type ActionUsageEntry = {
  count: number
  lastUsed: number
}

export type ActionUsageMap = Record<string, ActionUsageEntry>

function storageKey(scope: string): string {
  return `${STORAGE_PREFIX}:${scope}`
}

export function loadActionUsage(scope: string): ActionUsageMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(storageKey(scope))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as ActionUsageMap
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function recordActionUsage(scope: string, actionId: string): void {
  if (typeof window === 'undefined' || !actionId) return
  try {
    const usage = loadActionUsage(scope)
    const prev = usage[actionId]
    usage[actionId] = {
      count: (prev?.count ?? 0) + 1,
      lastUsed: Date.now(),
    }
    window.localStorage.setItem(storageKey(scope), JSON.stringify(usage))
  } catch {
    /* storage full / private mode */
  }
}

/**
 * Stable sort: most-used first, then default declaration order for
 * never-used actions.
 */
export function sortByActionUsage<T>(
  scope: string,
  items: readonly T[],
  getId: (item: T) => string,
): T[] {
  return sortItemsByActionUsage(loadActionUsage(scope), items, getId)
}

function sortItemsByActionUsage<T>(
  usage: ActionUsageMap,
  items: readonly T[],
  getId: (item: T) => string,
): T[] {
  const indexed = items.map((item, index) => ({ item, index, id: getId(item) }))
  indexed.sort((a, b) => {
    const entryA = usage[a.id]
    const entryB = usage[b.id]
    const usedA = Boolean(entryA && entryA.count > 0)
    const usedB = Boolean(entryB && entryB.count > 0)
    if (usedA && usedB) {
      if (entryB!.count !== entryA!.count) return entryB!.count - entryA!.count
      return entryB!.lastUsed - entryA!.lastUsed
    }
    if (usedA && !usedB) return -1
    if (!usedA && usedB) return 1
    return a.index - b.index
  })
  return indexed.map((x) => x.item)
}

/** Wrap a click handler to record usage before invoking. */
export function withActionUsageTracking(
  scope: string,
  actionId: string,
  handler: () => void,
): () => void {
  return () => {
    recordActionUsage(scope, actionId)
    handler()
  }
}

/** React hook — re-sorts when the operator uses an action. */
export function useSortedByActionUsage<T>(
  scope: string,
  items: readonly T[],
  getId: (item: T) => string,
): { sorted: T[]; onActionUsed: (actionId: string) => void } {
  const [usage, setUsage] = useState<ActionUsageMap>(() => loadActionUsage(scope))
  useEffect(() => {
    const key = storageKey(scope)
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage && event.key === key) {
        setUsage(loadActionUsage(scope))
      }
    }
    setUsage(loadActionUsage(scope))
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [scope])
  const sorted = useMemo(
    () => sortItemsByActionUsage(usage, items, getId),
    [usage, items, getId],
  )
  const onActionUsed = useCallback(
    (actionId: string) => {
      recordActionUsage(scope, actionId)
      setUsage(loadActionUsage(scope))
    },
    [scope],
  )
  return { sorted, onActionUsed }
}
